/**
 * Inline editor for Recomendações Prioritárias.
 *
 * Props:
 *   items          : array of {ordem, titulo, descricao, horizonte}
 *   patchEndpoint  : full API path for PATCH (e.g. `/assessments/abc/ai-analyses/xyz/recommendations`)
 *   onSaved(updated) : called when persist succeeds — receives full updated analysis doc
 *   readOnly       : disables editing (e.g. for `client` role)
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const HORIZONTE_BG = {
  "Imediato (30 dias)": "bg-rose-100 text-rose-800",
  "Curto (90 dias)": "bg-orange-100 text-orange-800",
  "Médio (6 meses)": "bg-amber-100 text-amber-800",
  "Longo (12 meses)": "bg-emerald-100 text-emerald-800",
};

const HORIZONTE_OPTIONS = [
  "Imediato (30 dias)",
  "Curto (90 dias)",
  "Médio (6 meses)",
  "Longo (12 meses)",
];

export default function RecommendationsEditor({ items = [], patchEndpoint, onSaved, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(items);
  const [saving, setSaving] = useState(false);

  // Sync external updates when not editing
  useEffect(() => { if (!editing) setDraft(items); }, [items, editing]);

  const startEdit = () => { setDraft(items.map((r, i) => ({ ...r, ordem: r.ordem || i + 1 }))); setEditing(true); };
  const cancel = () => { setDraft(items); setEditing(false); };

  const update = (idx, field, value) => {
    setDraft((d) => d.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addItem = () => {
    setDraft((d) => [...d, { ordem: d.length + 1, titulo: "", descricao: "", horizonte: "Curto (90 dias)" }]);
  };

  const removeItem = (idx) => {
    setDraft((d) => d.filter((_, i) => i !== idx).map((r, i) => ({ ...r, ordem: i + 1 })));
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= draft.length) return;
    setDraft((d) => {
      const copy = [...d];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy.map((r, i) => ({ ...r, ordem: i + 1 }));
    });
  };

  const save = async () => {
    const cleaned = draft
      .filter((r) => (r.titulo || "").trim() || (r.descricao || "").trim())
      .map((r, i) => ({ ...r, ordem: i + 1 }));
    if (cleaned.length === 0) {
      toast.error("Mantenha pelo menos uma recomendação preenchida.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(patchEndpoint, { recomendacoes_prioritarias: cleaned });
      onSaved?.(r.data);
      toast.success("Recomendações atualizadas.");
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <article data-testid="recommendations-readonly">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            <i className="fa-solid fa-list-check mr-2 text-emerald-600"></i>
            Recomendações prioritárias
          </h3>
          {!readOnly && items.length > 0 && (
            <button onClick={startEdit} className="btn btn-ghost text-xs" data-testid="recommendations-edit-btn">
              <i className="fa-solid fa-pen-to-square mr-1"></i> Editar
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nenhuma recomendação registrada.</p>
        ) : (
          <ol className="space-y-3">
            {items.map((r, i) => (
              <li key={i} className="flex gap-3 bg-emerald-50/40 border-l-4 border-emerald-500 rounded-r-lg p-3">
                <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold">
                  {r.ordem || i + 1}
                </span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h4 className="font-bold text-slate-900">{r.titulo}</h4>
                    {r.horizonte && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${HORIZONTE_BG[r.horizonte] || "bg-slate-100 text-slate-700"}`}>
                        {r.horizonte}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{r.descricao}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </article>
    );
  }

  // Editing mode
  return (
    <article data-testid="recommendations-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">
          <i className="fa-solid fa-list-check mr-2 text-emerald-600"></i>
          Editar recomendações
        </h3>
        <div className="flex gap-2">
          <button onClick={cancel} disabled={saving} className="btn btn-secondary text-xs" data-testid="recommendations-cancel">
            Cancelar
          </button>
          <button onClick={save} disabled={saving} className="btn btn-primary text-xs" data-testid="recommendations-save">
            {saving ? <><i className="fa-solid fa-circle-notch fa-spin mr-1"></i>Salvando…</> : <><i className="fa-solid fa-check mr-1"></i>Salvar</>}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {draft.map((r, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-white space-y-2" data-testid={`recommendation-row-${i}`}>
            <div className="flex items-center gap-2">
              <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold">
                {i + 1}
              </span>
              <input
                type="text"
                value={r.titulo || ""}
                onChange={(e) => update(i, "titulo", e.target.value)}
                placeholder="Título da recomendação"
                className="flex-1 input text-sm font-semibold"
                data-testid={`recommendation-titulo-${i}`}
              />
              <select
                value={r.horizonte || ""}
                onChange={(e) => update(i, "horizonte", e.target.value)}
                className="input text-xs"
                data-testid={`recommendation-horizonte-${i}`}
              >
                <option value="">Sem horizonte</option>
                {HORIZONTE_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="btn btn-ghost px-2" title="Mover para cima" data-testid={`recommendation-up-${i}`}>
                  <i className="fa-solid fa-arrow-up text-xs"></i>
                </button>
                <button onClick={() => move(i, 1)} disabled={i === draft.length - 1} className="btn btn-ghost px-2" title="Mover para baixo" data-testid={`recommendation-down-${i}`}>
                  <i className="fa-solid fa-arrow-down text-xs"></i>
                </button>
                <button onClick={() => removeItem(i)} className="btn btn-ghost text-rose-600 px-2" title="Remover" data-testid={`recommendation-remove-${i}`}>
                  <i className="fa-solid fa-trash text-xs"></i>
                </button>
              </div>
            </div>
            <textarea
              value={r.descricao || ""}
              onChange={(e) => update(i, "descricao", e.target.value)}
              placeholder="Descrição detalhada (ações concretas, responsáveis, indicadores…)"
              rows={3}
              className="input text-sm w-full"
              data-testid={`recommendation-descricao-${i}`}
            />
          </div>
        ))}
      </div>

      <button onClick={addItem} className="btn btn-secondary text-xs mt-3" data-testid="recommendations-add">
        <i className="fa-solid fa-plus mr-1"></i> Adicionar recomendação
      </button>
    </article>
  );
}
